(ns dashboard.events.bookings
  (:require
   [clojure.string :refer [join]]
   [re-frame.core :as re-frame]
   [graphql-query.core :refer [graphql-query]]
   [dashboard.utils :refer
    [graphql-request-format index-of format-iso import-csv-file download-as-xlsx

     format-time escape secure-csv-lines]]
   [dashboard.appsync.core :as re-graph]
   [dashboard.interceptors :refer [standard-interceptors]]
   [dashboard.config :as config]
   ["date-fns" :refer [parse format]]))

(def booking-fields
  [:id :organizationId :eventId :roomId :start :end :productName :externalId
   :inviterName :boutiqueName :clientId :bookingId :guestFirstName :guestLastName
   :guestNumbers :guestFirstNameJp :guestLastNameJp :roomType :comment :posName
   :checkIn :enter :exit :checkOut :clean :cancel
   [:accompanyingGuests [:name :age :gender]]])

;; Create Event

(defn create-booking-query
  [{:keys [booking]}]
  {:operation {:operation/type :mutation
               :operation/name :addBooking}
   :queries [[:addBooking
              (-> booking
                  (dissoc :id :created :date :cancel :startTime :endTime
                          :checkInTime :checkOutTime)
                  (select-keys (flatten booking-fields))
                  (assoc
                   :comment (escape (:comment booking))
                   :start (format-iso (:start booking))
                   :end (format-iso (:end booking))
                   :checkIn (format-iso (:checkIn booking))
                   :enter (format-iso (:enter booking))
                   :exit (format-iso (:exit booking))
                   :checkOut (format-iso (:checkOut booking))
                   :clean (format-iso (:clean booking))))
              booking-fields]]})

(re-frame/reg-event-fx
 ::create-booking
 standard-interceptors
 (fn [{:keys [db]} [{:keys [booking]}]]
   {:graphql-request {:query (graphql-query (create-booking-query
                                             {:booking booking}))
                      :on-success [::create-booking-success]}}))

(re-frame/reg-event-fx
 ::create-booking-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Creating Booking"
       :intent :intent/danger
       :duration 10000
       :message (join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Booking Created"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly created " (:name (get-in res [:data :addBooking])))}
      :dispatch [::add-booking (get-in res [:data :addBooking])]})))

;; Update Booking

(defn update-booking-query
  [{:keys [booking]}]
  {:operation {:operation/type :mutation
               :operation/name :updateBooking}
   :queries [[:updateBooking
              (-> booking
                  (dissoc :organizationId :created :date :startTime :endTime
                          :checkInTime :checkOutTime)
                  (assoc
                   :comment (escape (:comment booking))
                   :start (format-iso (:start booking))
                   :end (format-iso (:end booking))
                   :checkIn (format-iso (:checkIn booking))
                   :enter (format-iso (:enter booking))
                   :exit (format-iso (:exit booking))
                   :checkOut (format-iso (:checkOut booking))
                   :clean (format-iso (:clean booking))
                   :cancel (format-iso (:cancel booking))))
              booking-fields]]})

(re-frame/reg-event-fx
 ::update-booking
 standard-interceptors
 (fn [{:keys [db]} [{:keys [booking]}]]
   {:graphql-request {:query (graphql-query (update-booking-query {:booking booking}))
                      :on-success [::update-booking-success]}}))

(re-frame/reg-event-fx
 ::update-booking-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Updating Booking"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Booking Updated"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly updated the booking "
                     (:name (get-in res [:data :updateBooking])))}
      :dispatch [::edit-booking (get-in res [:data :updateBooking])]})))

;; Create Booking

(defn delete-booking-query
  [{:keys [booking]}]
  {:operation {:operation/type :mutation
               :operation/name :deleteBooking}
   :queries [[:deleteBooking
              {:id (:id booking)}
              booking-fields]]})

(re-frame/reg-event-fx
 ::delete-booking
 standard-interceptors
 (fn [{:keys [db]} [{:keys [booking]}]]
   {:graphql-request {:query (graphql-query (delete-booking-query {:booking booking}))
                      :on-success [::delete-booking-success]}}))

(re-frame/reg-event-fx
 ::delete-booking-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   (if (some? (:errors res))
     {:show-notification!
      {:title "Error Deleting Booking"
       :intent :intent/danger
       :duration 10000
       :message (clojure.string/join "\n" (map :message (:errors res)))}}
     {:show-notification!
      {:title "Booking Deleted"
       :intent :intent/success
       :duration 10000
       :message (str "You successfuly deleted " (:name (get-in res [:data :deleteBooking])))}
      :dispatch [::remove-booking (get-in res [:data :deleteBooking])]})))

;; Add a booking to the list of bookings in the application db
(re-frame/reg-event-db
 ::add-booking
 standard-interceptors
 (fn [db [booking]]
   (update-in db [:event-organizer/bookings] conj booking)))

;; Edit a booking to the list of bookings in the application db
(re-frame/reg-event-db
 ::edit-booking
 standard-interceptors
 (fn [db [booking]]
   (let [booking-idx (index-of (map :id (:event-organizer/bookings db)) (:id booking))]
     (update-in db [:event-organizer/bookings booking-idx] merge booking))))

(re-frame/reg-event-db
 ::add-or-edit-booking
 standard-interceptors
 (fn [db [booking]]
   (let [booking-idx (index-of (map :id (:event-organizer/bookings db)) (:id booking))]
     (if (some? (:id booking))
       (if (nil? booking-idx)
         (update-in db [:event-organizer/bookings] conj booking)
         (update-in db [:event-organizer/bookings booking-idx] merge booking))
       db))))

;; Remove a booking from the list of bookings in the application db
(re-frame/reg-event-db
 ::remove-booking
 standard-interceptors
 (fn [db [booking]]
   (let [old-bookings (:event-organizer/bookings db)
         new-bookings (into [] (filter #(not= (:id %) (:id booking)) old-bookings))]
     (assoc db :event-organizer/bookings new-bookings))))

;; List Bookings

(defn list-bookings-by-event-query
  [{:keys [event-id]}]
  {:operation {:operation/type :query
               :operation/name :allBookingsByEvent}
   :queries [[:allBookingsByEvent
              {:eventId event-id}
              {:bookings booking-fields}]]})

(re-frame/reg-event-fx
 ::list-bookings-by-event
 standard-interceptors
 (fn [{:keys [db]} [{:keys [event-id]}]]
   (let [event-id (or event-id (get-in db [:event-organizer/selected :event-id]))]
     {:graphql-request {:query (graphql-query (list-bookings-by-event-query {:event-id event-id}))
                        :on-success [::list-bookings-by-event-success]}})))

(re-frame/reg-event-fx
 ::list-bookings-by-event-success
 standard-interceptors
 (fn [{:keys [db]} [res]]
   {:db (assoc db :event-organizer/bookings (get-in res [:data :allBookingsByEvent :bookings]))}))

;; Import bookings

(re-frame/reg-event-fx
 ::import-bookings-csv
 standard-interceptors
 (fn [{:keys [db]} [file]]
   (import-csv-file {:file file
                     :on-load-success #(re-frame/dispatch [::booking-csv-loaded %])
                     :on-load-fail #(re-frame/dispatch [:show-notification {:title "Failed to load CSV"
                                                                            :message (str %)}])})
   {:db (assoc db :event-organizer/bookings-file file)}))

(re-frame/reg-event-fx
 ::booking-csv-loaded
 standard-interceptors
 (fn [{:keys [db]} [csv-file]]
   {:db (assoc db :event-organizer/bookings-file-content csv-file)}))

(re-frame/reg-event-db
 ::remove-bookings-file
 standard-interceptors
 (fn [db []]
   (assoc db
          :event-organizer/bookings-file nil
          :event-organizer/bookings-file-content nil)))

;;-------------------------
;; Subscription

(re-frame/reg-event-fx
 ::subscribe-add-booking
 standard-interceptors
 (fn [{:keys [db]}]
   (let [event-id (get-in db [:event-organizer/selected :event-id])]
     (if (some? event-id)
       {:dispatch
        [::re-graph/subscribe
         :subscribe-add-booking-query
         (graphql-query {:operation {:operation/type :subscription
                                     :operation/name :onAddBooking}
                         :queries [[:onAddBooking
                                    {:eventId event-id}
                                    booking-fields]]})
         {}
         [::subscribe-add-booking-success]]}
       {}))))

(re-frame/reg-event-fx
 ::subscribe-add-booking-success
 standard-interceptors
 (fn [{:keys [db]} [{:keys [data errors] :as payload}]]
   (if (some? errors)
     {:show-notification! {:title "GraphQL Subscrption Error"
                           :intent :intent/danger
                           :duration 10000
                           :message (clojure.string/join "\n" (map :message (:errors errors)))}}
     {:dispatch [::add-booking-or-edit (:onAddBooking data)]})))

(re-frame/reg-event-fx
 ::unsubscribe-add-booking
 standard-interceptors
 (fn [{:keys [db]}]
   {:dispatch
    [::re-graph/unsubscribe
     :subscribe-add-booking-query]}))

(re-frame/reg-event-fx
 ::subscribe-update-booking
 standard-interceptors
 (fn [{:keys [db]}]
   (let [event-id (get-in db [:event-organizer/selected :event-id])]
     (if (some? event-id)
       {:dispatch
        [::re-graph/subscribe
         :subscribe-update-booking-query
         (graphql-query {:operation {:operation/type :subscription
                                     :operation/name :onUpdateBooking}
                         :queries [[:onUpdateBooking
                                    {:eventId event-id}
                                    booking-fields]]})
         {}
         [::subscribe-update-booking-success]]}
       {}))))

(re-frame/reg-event-fx
 ::subscribe-update-booking-success
 standard-interceptors
 (fn [{:keys [db]} [{:keys [data errors] :as payload}]]
   (if (some? errors)
     {:show-notification! {:title "GraphQL Subscrption Error"
                           :intent :intent/danger
                           :duration 10000
                           :message (clojure.string/join "\n" (map :message (:errors errors)))}}
     {:dispatch [::add-or-edit-booking (:onUpdateBooking data)]})))

(re-frame/reg-event-fx
 ::unsubscribe-update-booking
 standard-interceptors
 (fn [{:keys [db]}]
   {:dispatch
    [::re-graph/unsubscribe
     :subscribe-update-booking-query]}))

(re-frame/reg-event-fx
 ::subscribe-delete-booking
 standard-interceptors
 (fn [{:keys [db]}]
   (let [event-id (get-in db [:event-organizer/selected :event-id])]
     (if (some? event-id)
       {:dispatch
        [::re-graph/subscribe
         :subscribe-delete-booking-query
         (graphql-query {:operation {:operation/type :subscription
                                     :operation/name :onDeleteBooking}
                         :queries [[:onDeleteBooking
                                    {:eventId event-id}
                                    booking-fields]]})
         {}
         [::subscribe-delete-booking-success]]}
       {}))))

(re-frame/reg-event-fx
 ::subscribe-delete-booking-success
 standard-interceptors
 (fn [{:keys [db]} [{:keys [data errors] :as payload}]]
   (if (some? errors)
     {:show-notification! {:title "GraphQL Subscrption Error"
                           :intent :intent/danger
                           :duration 10000
                           :message (clojure.string/join "\n" (map :message (:errors errors)))}}
     {:dispatch [::remove-booking (:onDeleteBooking data)]})))

(re-frame/reg-event-fx
 ::unsubscribe-update-booking
 standard-interceptors
 (fn [{:keys [db]}]
   {:dispatch
    [::re-graph/unsubscribe
     :subscribe-update-booking-query]}))

(re-frame/reg-event-fx
 ::subscribe-bookings
 standard-interceptors
 (fn [{:keys [db]}]
   {:dispatch-n [[::subscribe-add-booking]
                 [::subscribe-update-booking]
                 [::subscribe-delete-booking]]}))

(re-frame/reg-event-fx
 ::unsubscribe-bookings
 standard-interceptors
 (fn [{:keys [db]}]
   {:dispatch-n [[::unsubscribe-create-booking]
                 [::unsubscribe-update-booking]
                 [::unsubscribe-delete-booking]]}))

;;-------------------------
;; CSV & XLS Exports

(re-frame/reg-event-fx
 ::excel-export
 standard-interceptors
 (fn [{:keys [db now]} [bookings event]]
   (let [filename (str (format now "yyyyMMdd") (:name event) "Booking.xlsx")]
     (download-as-xlsx
      filename
      (cons
       ["ID" "管理番号" "顧客ID" "予約商品名" "予約商品利用日" "BTQ担当者名" "BTQ名" "POS担当者名" "POS名" "チェックイン時間"
        "入室時間" "退室時間" "チェックアウト時間" "キャンセル"]
       (->> bookings
            (mapv #(vector
                    (:id %)
                    (secure-csv-lines (:externalId %))
                    (secure-csv-lines (:clientId %))
                    (secure-csv-lines (:productName %))
                    (format (:start %) "yyyy-MM-dd")
                    (secure-csv-lines (:inviterName %))
                    (secure-csv-lines (:boutiqueName %))
                    (secure-csv-lines (:posInviterName %))
                    (secure-csv-lines (:posName %))
                    (format-time (:checkIn %))
                    (format-time (:enter %))
                    (format-time (:exit %))
                    (format-time (:checkOut %))
                    (format-time (:cancel %)))))))
     {:dispatch [::excel-export-success]})))

(re-frame/reg-event-fx
 ::excel-export-success
 standard-interceptors
 (fn [{:keys [db]} [_]]
   {:show-notification!
    {:title "Excel Exported"
     :intent :intent/success
     :duration 10000
     :message (str "You successfuly exported a Booking Excel file")}}))
